import { Close } from '@mui/icons-material';
import { Box, Button, Checkbox, Divider, FormLabel, IconButton, Modal } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Bars } from 'react-loader-spinner';
import clsx from 'clsx';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { reportJob, reportProfile, setReportModal } from '../../actions/general';
import { useMessage } from '../../../base/MessageProvider';
import ReactTextInput from '../../../components/ControllerComponent/ReactTextInput';

const boxStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: 400,
    width: '90%',
    bgcolor: 'white',
    paddingX: 5,
    paddingY: 3,
    borderRadius: '18px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center' 
}

const validateSchema = Yup.object().shape({
    reason: Yup.string().max(150, "Reason should not be more than 150 characters"),
})


const ReportModal = () => {
    const dispatch = useDispatch();
    const alert = useMessage();

    const loading = useSelector((state) => state.artistGeneralReducer?.loading);
    const reportTypesList = useSelector((state) => state.artistGeneralReducer?.reportTypes);
    const modalState = useSelector((state) => state.artistGeneralReducer?.reportModal);

    const { control, handleSubmit, clearErrors, formState: { errors }, reset, setValue, watch } = useForm({
        resolver: yupResolver(validateSchema),
        defaultValues: { reason: '', reportType: undefined }
    });

    const isJob = modalState?.type === 'job';
    const reportType = watch("reportType");

    useEffect(() => {
        const init = async () => {
            clearErrors(); reset();
        }
        init();
    }, [])

    const onClose = () => {
        dispatch(setReportModal({
            open: false, data: null, type: undefined
        }));
    }

    const onChangeOptions = (selectedValue) => (event) => {
        if (reportType !== selectedValue) setValue("reportType", selectedValue);
        else setValue("reportType", undefined);
    }

    const successCallback = async (res) => {
        alert.showSuccess(res.message);
    }

    const errorCallback = async (error) => {
        alert.showError(error?.message ?? "Something went wrong!!");
    }

    const onSubmit = async (data) => {
        const reportedId = isJob ? modalState?.data?.jobId : modalState?.data?.userId;
        const payload = { 
            reportedId: reportedId, 
            id: data.reportType,
            reportReason: data.reason
        };
        if (isJob) dispatch(reportJob(payload, successCallback, errorCallback));
        else dispatch(reportProfile(payload, successCallback, errorCallback));
    }

    const DISABLE_SUBMIT = loading || !reportType

    return (
        <Modal disableAutoFocus sx={{ bgcolor: '#F4F4F499'}} hideBackdrop open={modalState?.open}>
            <Box className="shadow-[0_0_14px_grey]" sx={boxStyle}>
                <IconButton onClick={onClose} className='absolute top-3 right-3' size='small'> 
                    <Close className='text-xl' />
                </IconButton>
                <Box className="mb-4">
                    <h2 className="font-['Poppins-Medium'] font-[500] text-[18px]">REPORT</h2>
                </Box>
                <div className="flex flex-col gap-3 mt-3 w-full">
                    <h2 className="font-['Poppins-Medium'] font-[500] text-[15px]">Why are you reporting this {isJob ? 'Job' : 'User'}?</h2>
                    <Box className="w-full">
                        {reportTypesList?.map((item) => (
                            <Box className="flex justify-between items-center gap-3 max-w-[380px] w-full">
                                <FormLabel className="font-['CG-Medium'] case normal-case font-[400] text-[13px] text-[#000]">{item.name}</FormLabel>
                                <Checkbox size="small" sx={{ '&.Mui-checked': { color: "#2F80ED" } }} checked={item.id === reportType} onChange={onChangeOptions(item.id)} />
                            </Box>
                        ))}
                        <Box className="my-2">
                            <ReactTextInput
                                hookFormControl={control}
                                varName="reason"
                                key={'reason'}
                                isMultiLine rows={2}
                                maxLength={150}
                                errorMessage={errors?.reason?.message}
                            />
                        </Box>
                    </Box>
                </div>
                <Button onClick={handleSubmit(onSubmit)} disabled={DISABLE_SUBMIT} className={clsx("bg-[#2F80ED] normal-case flex gap-3 justify-center text-[#FFF] w-full rounded-full my-3 font-['Poppins-Medium'] font-[500]", { "bg-opacity-50": DISABLE_SUBMIT })}>
                    Submit Report
                    <Bars
                        height="20"
                        width={'20'}
                        color="#FFFFFF"
                        ariaLabel="bars-loading"
                        visible={loading}
                    />
                </Button>
            </Box>
        </Modal>
    );
}

export default React.memo(ReportModal, (prevProps, nextProps) => {
    return prevProps.open === nextProps.open
});